import {useLocation, useParams} from 'react-router-dom'
import css from './SectionTask.module.css'
import {TextNorm} from '../../../../ui/Text'
import Link from '../../../../app/Link/Link'
import Avatar from '../../../../ui/Avatar/Avatar'
import AssigneeDropdown from '../../../AssigneeDropdown/AssigneeDropdown'
import {useWorkspace} from '../../../../../contexts/WorkspaceContext'
import type {WorkspaceMember} from '../../../../../contexts/WorkspaceContext'
import {useEffect, useRef, type CSSProperties} from 'react'
import AvatarPlaceholderIcon from '../../../../../assets/icons/icon-avatar-placeholder.svg?react'
import {useAppDispatch, useAppSelector} from '../../../../../store/hooks'
import {subscribeTaskActivitySaga} from '../../../../../store/activity/activitySlice'
import {prefetchDescriptionSaga} from '../../../../../store/tasks/tasksSlice'
import {prefetchCommentsSaga} from '../../../../../store/comments/commentsSlice'
import {usePublicShare} from '../../../../../contexts/PublicShareContext'
import {taskPath} from '@/utils/taskPath'
import LabelChip from '@/components/ui/LabelChip/LabelChip'
import type {ResolvedLabel} from '@/utils/taskLabels'

interface SectionTaskTitleProps {
    name: string
    assignee: WorkspaceMember | null | undefined
    statusColor?: string
    isCompleted?: boolean
    number: number
    taskId: string
    // Optimistic tasks aren't saved yet (no real number) — they must not be linkable (SCD-948).
    pending?: boolean
    onAssign?: (assigneeId: string | null) => void
    labels?: ResolvedLabel[]
    // Supplied only by the project list, where a chip click filters. Elsewhere (My tasks, the
    // public share view) the chips render as plain text.
    onLabelClick?: (labelId: string) => void
    activeLabelIds?: string[]
}

// One chip plus "+N" — same rule the task modal's picker uses. Chips are Add-task-sized pills
// now, so showing several would compete with the task name for the row. All of them are visible
// in the modal's label menu.
const VISIBLE_LABEL_COUNT = 1

const SectionTaskTitle = ({name, assignee, statusColor, number, taskId, pending, onAssign, labels, onLabelClick, activeLabelIds}: SectionTaskTitleProps) => {
    const dispatch = useAppDispatch()
    const location = useLocation()
    const {workspace: workspaceUrlId} = useParams<{workspace: string}>()
    const {workspace} = useWorkspace()
    const publicShare = usePublicShare()
    // In the public read-only view tasks aren't clickable — there's no task modal there.
    // taskPath returns null for a pending / un-persisted task, so it renders as plain text.
    const href = publicShare
        ? undefined
        : taskPath({ workspaceUrlId, prefix: workspace?.workspaceTaskPrefixId, number, pending }) ?? undefined

    // commentCount is maintained server-side; only warm comments for tasks that actually have any.
    const commentCount = useAppSelector(state => state.tasks.byId[taskId]?.commentCount ?? 0)
    const workspaceId = workspace?.id

    // Prefetch the description on a deliberate hover (~120ms) so opening the task shows it
    // instantly — but not while sweeping the mouse across the list, which would fetch every
    // task passed over. mousedown also prefetches immediately, covering a fast click with no pause.
    // Comments are warmed alongside, but only when the task has some (commentCount > 0).
    const prefetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const clearPrefetch = () => {
        if (prefetchTimer.current) { clearTimeout(prefetchTimer.current); prefetchTimer.current = null }
    }
    const prefetch = () => {
        // force: a description changed after the tab loaded gives the open tab no signal (a description
        // edit doesn't touch the task doc), so always re-read on a deliberate hover/click (SCD-927).
        dispatch(prefetchDescriptionSaga({ taskId, force: true }))
        if (commentCount > 0 && workspaceId) dispatch(prefetchCommentsSaga({ taskId, workspaceId }))
    }
    const handleEnter = () => {
        if (publicShare) return
        clearPrefetch()
        prefetchTimer.current = setTimeout(prefetch, 120)
    }
    useEffect(() => clearPrefetch, [])

    const titleStyle: CSSProperties | undefined = statusColor ? {
        background: statusColor
    } : undefined

    const text = name || 'Untitled'
    const lastSpaceIndex = text.lastIndexOf(' ')

    const leadingText = lastSpaceIndex > 0 ? text.slice(0, lastSpaceIndex) + ' ' : null
    const lastWord = lastSpaceIndex > 0 ? text.slice(lastSpaceIndex + 1) : text

    // When a status color is set, round/pad only the outer edges of the highlight so
    // the two title spans read as one continuous pill (see SectionTask.module.css).
    const hasStatus = !!statusColor
    const leadingClass = hasStatus ? `${css.title} ${css.title_start}` : css.title
    const lastWordClass = hasStatus
        ? `${css.title} ${leadingText ? css.title_end : css.title_solo}`
        : css.title

    const visibleLabels = labels?.slice(0, VISIBLE_LABEL_COUNT) ?? []
    const hiddenLabelCount = (labels?.length ?? 0) - visibleLabels.length
    const labelsElement = visibleLabels.length > 0 ? (
        <span className={css.labels}>
            {visibleLabels.map(label => (
                <LabelChip
                    key={label.id}
                    name={label.name}
                    color={label.color}
                    active={activeLabelIds?.includes(label.id)}
                    onClick={onLabelClick ? () => onLabelClick(label.id) : undefined}
                    testId="task-label-chip"
                />
            ))}
            {hiddenLabelCount > 0 && <span className={css.labels_more}>+{hiddenLabelCount}</span>}
        </span>
    ) : null

    const avatarElement = onAssign ? (
        <AssigneeDropdown assigneeId={assignee?.userId ?? null} onSelect={onAssign}>
            {({onClick, isOpen}) => (
                assignee ? (
                    <span className={css.assignee} onClick={onClick}>
                        <Avatar
                            title={assignee.username}
                            color={assignee.color}
                            avatar={assignee.avatarImage}
                        />
                    </span>
                ) : (
                    <span
                        className={`${css.assignee_placeholder}${isOpen ? ` ${css.assignee_placeholder_visible}` : ''}`}
                        onClick={onClick}
                    >
                        <AvatarPlaceholderIcon/>
                    </span>
                )
            )}
        </AssigneeDropdown>
    ) : assignee ? (
        <span className={`${css.assignee} ${css.assignee_disabled}`}>
            <Avatar
                title={assignee.username}
                color={assignee.color}
                avatar={assignee.avatarImage}
            />
        </span>
    ) : null

    return (
        <div
            className={css.title_wrapper}
            onMouseEnter={handleEnter}
            onMouseLeave={clearPrefetch}
            onMouseDown={() => {
                if (publicShare) return
                clearPrefetch()
                prefetch()
                dispatch(subscribeTaskActivitySaga(taskId))
            }}
        >
            <TextNorm>
                {leadingText && href ? (
                    <Link href={href} state={{backgroundLocation: location}} className={leadingClass}
                          style={titleStyle}>{leadingText}</Link>
                ) : leadingText ? (
                    <span className={leadingClass} style={titleStyle}>{leadingText}</span>
                ) : null}
                <span className={css.title_last_word}>
                    {href ? (
                        <Link href={href} state={{backgroundLocation: location}} className={lastWordClass}
                              style={titleStyle}>{lastWord}</Link>
                    ) : (
                        <span className={lastWordClass} style={titleStyle}>{lastWord}</span>
                    )}
                    {labelsElement}
                    {avatarElement}
                </span>
            </TextNorm>
        </div>
    )
}

export default SectionTaskTitle
